import React, {
    FC,
    SyntheticEvent,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import {
    BuildType,
    MeData,
    NetworkBuild,
    PlatformConfigData,
    ShareLink,
    ShareLinkVariables,
    TempLink,
    TempLinkVariables
} from "../../models/types";
import {
    Badge,
    Button,
    Card,
    Col,
    Collapse,
    Container,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import { AlertModal } from "../modals/AlertModal";
import { useLocalState } from "../../graphql/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChartLine,
    faCheck,
    faExclamationCircle,
    faExclamationTriangle,
    faFileArrowDown,
    faPlay,
    faQrcode,
    faShareSquare
} from "@fortawesome/free-solid-svg-icons";
import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import {
    GET_ME,
    GET_PLATFORM_CONFIG,
    GET_PUBLIC_LINK,
    GET_TEMP_LINK
} from "../../graphql/queries";
import { LanguageTestDropdown } from "../dropdowns/LanguageTestDropdown";
import { BuildAnalyticsInfo } from "./BuildAnalyticsInfo";
import {
    saveBuildPlayState,
    updateBuildPlaySet,
    updateNotificationState
} from "../../common/Helpers";
import { useParams } from "react-router-dom";
import cx from "classnames";
import styles from "./BatchBuildReport.module.scss";

interface Props {
    buildId: number;
    networkBuild: NetworkBuild;
    duplicates: string[] | undefined;
    localizations: string[] | undefined;
    statsKey: string;
    s3Key: string;
    openModal: (job: string, id: number, buildId: number | undefined) => void;
    arrayIndex: number;
    buildType: BuildType;
    showFullBuildName: boolean;
    buildPlayHistory: Set<number>;
    cardColor?: string;
}

const getBadgeVariant = (size: number | undefined) => {
    if (typeof size === "number") {
        switch (size) {
            case 2:
                return "density-1";
            case 3:
                return "density-2";
            case 5:
                return "density-3";
            default:
                return "info";
        }
    }
    return "info";
};

export const NetworkBuildReport: FC<Props> = ({
    buildId,
    networkBuild,
    statsKey,
    duplicates,
    localizations,
    s3Key,
    openModal,
    arrayIndex,
    buildType,
    showFullBuildName,
    buildPlayHistory,
    cardColor
}) => {
    const { data: { me } = {} } = useQuery<MeData>(GET_ME, {
        fetchPolicy: "cache-first"
    });
    const { data: { getPlatformConfig } = {} } =
        useQuery<PlatformConfigData>(GET_PLATFORM_CONFIG);
    const buildToolWarningsRef = useRef<HTMLDivElement | null>(null);
    const currentLocalization = useRef<string | undefined>(undefined);
    const [played, updatePlayed] = useState(
        buildPlayHistory.has(Number(networkBuild.id))
    );
    const client = useApolloClient();
    const state = useLocalState();
    const { productId } = useParams<{
        productId: string;
    }>();
    const [fetchTempLink, updateFetchTempLink] = useState<{
        secureLink: boolean;
        qrCode: boolean;
    }>({ secureLink: false, qrCode: false });
    const [showDetails, setDetails] = useState(false);
    const [showQR, setShowQR] = useState(false);

    // @ts-ignore
    const [getShareLink, { data: { generatePublicLink } = {} }] = useLazyQuery<
        ShareLink,
        ShareLinkVariables
    >(GET_PUBLIC_LINK, {
        fetchPolicy: "no-cache",
        variables: { buildId: buildId, fileName: networkBuild.filename }
    });

    // @ts-ignore
    const [getTempLink, { data: { generateLink } = {} }] = useLazyQuery<
        TempLink,
        TempLinkVariables
    >(GET_TEMP_LINK, {
        fetchPolicy: "no-cache",
        refetchWritePolicy: "overwrite"
    });

    const memoizedShowToast = useCallback((message: string) => {
        showToast(message);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateBuildPlayState = (remove = false) => {
        if (!me?.testerMode || (!remove && played)) {
            return;
        }
        const updated = updateBuildPlaySet(
            buildPlayHistory,
            Number(networkBuild.id),
            remove
        );
        saveBuildPlayState(buildId, updated);
        updatePlayed(!remove);
    };

    useEffect(() => {
        if (
            (fetchTempLink.secureLink || fetchTempLink.qrCode) &&
            generateLink &&
            generateLink.link
        ) {
            if (fetchTempLink.secureLink) {
                const win = window.open(generateLink.link, "_blank");
                if (win !== null) {
                    win.focus();
                }
            } else {
                updateBuildPlayState();
                setShowQR(true);
            }
            updateFetchTempLink({ secureLink: false, qrCode: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generateLink]);

    useEffect(() => {
        const copy = async (link: string) => {
            try {
                if (currentLocalization.current !== undefined) {
                    link = `${link}?test_lang=${currentLocalization.current}`;
                }
                await navigator.clipboard.writeText(link);
                const message =
                    currentLocalization.current !== undefined
                        ? `Link Copied to Clipboard with localization ${currentLocalization.current}`
                        : "Link Copied to Clipboard";
                memoizedShowToast(message);

                client.cache.writeQuery({
                    query: GET_PUBLIC_LINK,
                    variables: {
                        buildId: buildId,
                        fileName: networkBuild.filename
                    },
                    // @ts-ignore
                    data: {
                        generatePublicLink: { ...generatePublicLink, link: "" }
                    }
                });
                updateBuildPlayState();
            } catch (error) {
                console.log("[DEBUG] copy error ", error);
            }
        };
        if (generatePublicLink && generatePublicLink.link) {
            copy(generatePublicLink.link);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [generatePublicLink]);

    const showToast = (message: string) => {
        updateNotificationState(client, false, true, {
            success: true,
            header: "Generate Link",
            message: message,
            delay: 2000
        });
    };

    const getStatsFile = () => {
        currentLocalization.current = undefined;
        updateFetchTempLink({
            secureLink: true,
            qrCode: false
        });
        getTempLink({
            variables: {
                productId: Number(productId),
                key: statsKey
            }
        });
    };

    const isQrSupported = getPlatformConfig?.qrNetworks.includes(
        networkBuild.network.toLowerCase()
    );

    const handleQrLink = (link: string) => {
        const splitLink = link.split("?");
        return `${unescape(splitLink[0])}?${splitLink[1]}`;
    };

    const Placeholder = () => (
        <div className="m-2 text-disabled">Mutator build</div>
    );

    const linkElements = () => {
        if (buildType === BuildType.Mutator) {
            return <Placeholder />;
        }
        if (networkBuild.filename?.includes(".zip")) {
            return (
                <OverlayTrigger
                    delay={{ show: 550, hide: 100 }}
                    overlay={<Tooltip>Download ZIP-file</Tooltip>}
                >
                    <Button
                        size="sm"
                        className="mb-2 mt-2 ms-1"
                        onClick={(event: any) => {
                            event.stopPropagation();
                            currentLocalization.current = undefined;
                            updateFetchTempLink({
                                secureLink: true,
                                qrCode: false
                            });
                            getTempLink({
                                variables: {
                                    productId: Number(productId),
                                    key: s3Key
                                }
                            });
                        }}
                    >
                        <FontAwesomeIcon icon={faFileArrowDown} size="lg" />
                    </Button>
                </OverlayTrigger>
            );
        } else {
            return (
                <>
                    {isQrSupported ? (
                        <OverlayTrigger
                            delay={{ show: 550, hide: 100 }}
                            overlay={
                                <Tooltip>
                                    Generate a QR code with a 1-hour temporary
                                    link to this ad
                                </Tooltip>
                            }
                        >
                            <Button
                                size="sm"
                                className="mb-2 mt-2 ms-1"
                                onClick={(
                                    event: React.MouseEvent<HTMLElement>
                                ) => {
                                    event.stopPropagation();
                                    updateFetchTempLink({
                                        secureLink: false,
                                        qrCode: true
                                    });
                                    currentLocalization.current = undefined;
                                    getTempLink({
                                        variables: {
                                            productId: Number(productId),
                                            key: s3Key
                                        }
                                    });
                                }}
                            >
                                <FontAwesomeIcon icon={faQrcode} size="lg" />
                            </Button>
                        </OverlayTrigger>
                    ) : null}
                    {state.developerMode && (
                        <OverlayTrigger
                            delay={{ show: 550, hide: 100 }}
                            overlay={
                                <Tooltip>
                                    Create a permanent shareable link and copy
                                    it to the clipboard
                                </Tooltip>
                            }
                        >
                            <div>
                                <LanguageTestDropdown
                                    id={`language-dropdown-${networkBuild.id}`}
                                    header={
                                        <FontAwesomeIcon
                                            icon={faShareSquare}
                                            size="1x"
                                        />
                                    }
                                    className="mb-2 mt-2 ms-1"
                                    localizations={localizations}
                                    callback={(
                                        localization: string | undefined
                                    ) => {
                                        if (
                                            localization &&
                                            localization !== "Default"
                                        ) {
                                            currentLocalization.current =
                                                localization;
                                        } else {
                                            currentLocalization.current =
                                                undefined;
                                        }
                                        getShareLink();
                                    }}
                                    showInfoModal={true}
                                />
                            </div>
                        </OverlayTrigger>
                    )}
                    <OverlayTrigger
                        overlay={
                            <Tooltip>
                                Open this ad in a new tab using a 1-hour
                                temporary link
                            </Tooltip>
                        }
                        delay={{ show: 550, hide: 100 }}
                    >
                        <div>
                            <LanguageTestDropdown
                                id={`language-dropdown-temp-${networkBuild.id}`}
                                header={
                                    <FontAwesomeIcon icon={faPlay} size="1x" />
                                }
                                className="mb-2 mt-2 ms-1"
                                localizations={localizations}
                                callback={(
                                    localization: string | undefined
                                ) => {
                                    if (
                                        localization &&
                                        localization !== "Default"
                                    ) {
                                        currentLocalization.current =
                                            localization;
                                    } else {
                                        currentLocalization.current = undefined;
                                    }
                                    updateFetchTempLink({
                                        secureLink: true,
                                        qrCode: false
                                    });
                                    getTempLink({
                                        variables: {
                                            productId: Number(productId),
                                            key: s3Key,
                                            localization:
                                                currentLocalization.current !==
                                                undefined
                                                    ? localization
                                                    : undefined
                                        }
                                    });
                                    updateBuildPlayState();
                                }}
                            />
                        </div>
                    </OverlayTrigger>
                </>
            );
        }
    };

    const isDuplicate =
        networkBuild.fileHash &&
        duplicates &&
        duplicates.includes(networkBuild.fileHash);
    const logIndex = arrayIndex;

    const getMBsize = () => {
        if (!networkBuild.byteSize) {
            return;
        }

        const byteSizeMB = networkBuild.byteSize / 1048576;

        return byteSizeMB.toLocaleString(undefined, {
            maximumSignificantDigits: 2
        });
    };

    return (
        <Card className={`text-contrast ${cardColor} m-1`}>
            {showQR ? (
                <AlertModal
                    visible={() => {
                        setShowQR(false);
                    }}
                    component={
                        <img
                            alt={`https://api.qrserver.com/v1/create-qr-code/?size=350x350&data=${escape(
                                generateLink && generateLink.link
                                    ? handleQrLink(generateLink.link)
                                    : ""
                            )}`}
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=350x350&data=${escape(
                                generateLink && generateLink.link
                                    ? handleQrLink(generateLink.link)
                                    : ""
                            )}`}
                        />
                    }
                    header={<h5>QR Code</h5>}
                    footer={
                        <>
                            <p className="text-center">
                                <b>Please note!</b> Unity Playable Tester App
                                might misread the QR link. You might have to try
                                multiple times to get the correct url.
                            </p>
                            <p>This link is temporary and valid for 1 hour.</p>
                        </>
                    }
                />
            ) : null}
            <Card.Header
                className={cx("pb-1 pt-1 px-3", {
                    mouseHover: state.developerMode
                })}
                onClick={event => {
                    if (event.defaultPrevented) {
                        return;
                    }
                    if (state.developerMode) {
                        setDetails(!showDetails);
                    }
                }}
            >
                <Row className="d-flex align-items-center g-1">
                    <Col
                        className={cx(
                            "text-start mt-2 mt-md-0 bread-text col-12",
                            {
                                "col-md-4": !showFullBuildName,
                                "col-md-7": showFullBuildName
                            }
                        )}
                    >
                        <span>
                            {showFullBuildName ? (
                                <span className={styles.fullBuildName}>
                                    {networkBuild.buildName}
                                </span>
                            ) : (
                                `${networkBuild.variationName} ${
                                    networkBuild.variation || ""
                                } ${networkBuild.duration || ""}`
                            )}
                        </span>
                    </Col>
                    <Col
                        className={cx("text-start col-7", {
                            "col-md-5": !showFullBuildName,
                            "col-md-2": showFullBuildName
                        })}
                    >
                        <OverlayTrigger
                            delay={{ show: 550, hide: 100 }}
                            overlay={
                                <Tooltip>
                                    Ad network this build is targeted for
                                </Tooltip>
                            }
                        >
                            <Badge
                                bg="purple"
                                className={cx("ms-1", styles.bigBadge)}
                            >
                                {networkBuild.network}
                            </Badge>
                        </OverlayTrigger>
                        <OverlayTrigger
                            delay={{ show: 550, hide: 100 }}
                            overlay={<Tooltip>Size</Tooltip>}
                        >
                            <Badge
                                bg={getBadgeVariant(networkBuild.size)}
                                className={cx(
                                    "ms-2 text-white",
                                    styles.bigBadge
                                )}
                            >
                                {networkBuild.size}MB
                            </Badge>
                        </OverlayTrigger>
                        {state.developerMode &&
                            networkBuild.analyticsEnabled && (
                                <OverlayTrigger
                                    delay={{ show: 550, hide: 100 }}
                                    overlay={
                                        <Tooltip>Analytics enabled</Tooltip>
                                    }
                                >
                                    <Badge
                                        bg="analytics"
                                        className={cx("ms-2 text-white")}
                                    >
                                        <FontAwesomeIcon
                                            icon={faChartLine}
                                            size="1x"
                                        />
                                    </Badge>
                                </OverlayTrigger>
                            )}
                        {networkBuild.buildToolWarnings &&
                        networkBuild.buildToolWarnings.length > 0 &&
                        state.developerMode ? (
                            <OverlayTrigger
                                delay={{ show: 550, hide: 100 }}
                                overlay={
                                    <Tooltip>Show build tool warnings</Tooltip>
                                }
                            >
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    size={"lg"}
                                    className={"text-warning ms-2 mouseHover"}
                                    onClick={(event: any) => {
                                        event.stopPropagation();
                                        if (!showDetails) {
                                            setDetails(true);
                                        }
                                        setTimeout(
                                            () =>
                                                buildToolWarningsRef.current?.scrollIntoView(
                                                    { behavior: "smooth" }
                                                ),
                                            400
                                        );
                                    }}
                                />
                            </OverlayTrigger>
                        ) : null}
                        {networkBuild.warnings?.includes(logIndex) ? (
                            <OverlayTrigger
                                delay={{ show: 550, hide: 100 }}
                                overlay={<Tooltip>Show warnings</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faExclamationTriangle}
                                    size={"lg"}
                                    className={"text-warning ms-2 mouseHover"}
                                    onClick={(event: any) => {
                                        event.stopPropagation();
                                        openModal("build_job", -1, logIndex);
                                    }}
                                />
                            </OverlayTrigger>
                        ) : null}
                        {networkBuild.errors?.includes(logIndex) ? (
                            <OverlayTrigger
                                delay={{ show: 550, hide: 100 }}
                                overlay={<Tooltip>Show errors</Tooltip>}
                            >
                                <FontAwesomeIcon
                                    icon={faExclamationCircle}
                                    size={"lg"}
                                    className={"text-danger ms-2 mouseHover"}
                                    onClick={(event: any) => {
                                        event.stopPropagation();
                                        openModal("build_job", -1, logIndex);
                                    }}
                                />
                            </OverlayTrigger>
                        ) : null}
                        {isDuplicate ? (
                            <OverlayTrigger
                                delay={{ show: 550, hide: 100 }}
                                overlay={<Tooltip>Duplicate build</Tooltip>}
                            >
                                <Badge
                                    bg="danger"
                                    className={cx("ms-2", styles.bigBadge)}
                                >
                                    DUP
                                </Badge>
                            </OverlayTrigger>
                        ) : null}
                    </Col>
                    <Col className="col-2 col-md-1">
                        {me?.testerMode ? (
                            <FontAwesomeIcon
                                onClick={e => {
                                    e.preventDefault();
                                    updateBuildPlayState(played);
                                }}
                                icon={faCheck}
                                className={cx({
                                    "text-disabled": !played,
                                    "text-success": played
                                })}
                            />
                        ) : null}
                    </Col>
                    <Col className="col-3 col-md-2 d-flex justify-content-end">
                        {linkElements()}
                    </Col>
                </Row>
            </Card.Header>
            {state.developerMode ? (
                <Collapse in={showDetails}>
                    <div>
                        <Card.Body>
                            <Card className="mb-1">
                                <Card.Header>
                                    <strong className="text-muted">
                                        Basic Info
                                    </strong>
                                </Card.Header>
                                <Card.Body className="text-contrast bread-text">
                                    <p
                                        className={cx({
                                            "text-danger": isDuplicate
                                        })}
                                    >
                                        <b className="text-muted">Hash:</b>{" "}
                                        {networkBuild.fileHash}
                                    </p>
                                    <p>
                                        <b className="text-muted">ByteSize:</b>{" "}
                                        {networkBuild.byteSize?.toLocaleString()}{" "}
                                        ({getMBsize()} MB)
                                    </p>
                                    {networkBuild.customPath !== "" ? (
                                        <p>
                                            <b className="text-muted">Path:</b>{" "}
                                            {networkBuild.customPath}
                                        </p>
                                    ) : null}
                                    <p>
                                        <b className="text-muted">
                                            Included Sets:
                                        </b>{" "}
                                        {!networkBuild.includedSets ||
                                        networkBuild.includedSets.length === 0
                                            ? "-"
                                            : networkBuild.includedSets?.join(
                                                  ", "
                                              )}
                                    </p>
                                    <p>
                                        <b className="text-muted">
                                            Excluded Sets:
                                        </b>{" "}
                                        {!networkBuild.excludedSets ||
                                        networkBuild.excludedSets.length === 0
                                            ? "-"
                                            : networkBuild.excludedSets?.join(
                                                  ", "
                                              )}
                                    </p>
                                </Card.Body>
                            </Card>
                            {networkBuild.analyticsEnabled ? (
                                <BuildAnalyticsInfo
                                    batchId={networkBuild.analyticsBatchId}
                                    buildId={networkBuild.analyticsBuildId}
                                    analyticsVersion={
                                        networkBuild.analyticsVersion
                                    }
                                />
                            ) : null}
                            {networkBuild.buildToolWarnings &&
                            networkBuild.buildToolWarnings.length > 0 &&
                            state.developerMode ? (
                                <Card className="mt-2">
                                    <Card.Header ref={buildToolWarningsRef}>
                                        <strong className="text-muted">
                                            Build Tool Warnings
                                        </strong>
                                    </Card.Header>
                                    <Card.Body className="bread-text">
                                        <p className="text-warning">
                                            {networkBuild.buildToolWarnings.join(
                                                ", "
                                            )}
                                        </p>
                                    </Card.Body>
                                </Card>
                            ) : null}
                            {networkBuild.warnings?.length > 0 &&
                            !networkBuild.warnings.includes(logIndex) &&
                            !networkBuild.errors.includes(logIndex) ? (
                                <Card className="mt-2">
                                    <Card.Header>
                                        <strong className="text-muted">
                                            Build Warnings
                                        </strong>
                                    </Card.Header>
                                    <Card.Body>
                                        <p className="text-warning">
                                            {networkBuild.warnings.join(", ")}
                                        </p>
                                    </Card.Body>
                                </Card>
                            ) : null}
                            {networkBuild.statsFile && state.developerMode ? (
                                <Container className="m-0 mt-2 p-0">
                                    <span
                                        className="ps-2 mouseHover mouseHoverLink text-primary"
                                        onClick={(event: SyntheticEvent) => {
                                            event.preventDefault();
                                            getStatsFile();
                                        }}
                                    >
                                        <Button>Build Stats</Button>
                                    </span>
                                </Container>
                            ) : null}
                        </Card.Body>
                    </div>
                </Collapse>
            ) : null}
        </Card>
    );
};
